import React from 'react';
import Fade from 'react-reveal/Fade';

import { ImplicitAuthTarget } from 'types/implicitAuthTypes';
import { ContentLayoutComponent } from 'design/contentLayout';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { ImplicitAuthEmailForm } from 'containers/ImplicitAuthEmailForm';
import { ImplicitAuthProviderForm } from 'containers/ImplicitAuthProviderForm';
import { BackControlComponent } from 'design/backControl';
import { MarginComponent } from 'design/margin';
import { ANIMATION_DEFAULT_DURATION } from 'utils/constants';
import { ParagraphComponent } from 'design/paragraph';

type Props = {
  target?: ImplicitAuthTarget;
  onEmailConfirmed(): void;
};

export const ImplicitAuthWidget = (props: Props) => {
  const { target, onEmailConfirmed } = props;

  const { implicitAuthForm: ownDictionary } = useIntlDictionary();

  const [emailProviderSelected, setEmailProviderSelected] = React.useState(
    false
  );

  const openProviderForm = React.useCallback(() => {
    setEmailProviderSelected(false);
  }, []);

  const openEmailForm = React.useCallback(() => {
    setEmailProviderSelected(true);
  }, []);

  const titleText = React.useMemo(() => {
    switch (target) {
      case 'DEMO':
        return ownDictionary.demoTitleText;
      case 'PROD':
        return ownDictionary.prodTitleText;
      case 'PARTNER':
        return ownDictionary.partnerTitleText;
      default:
        return ownDictionary.mainTitleText;
    }
  }, [
    ownDictionary.mainTitleText,
    ownDictionary.demoTitleText,
    ownDictionary.partnerTitleText,
    ownDictionary.prodTitleText,
    target,
  ]);

  const descriptionText = React.useMemo(() => {
    switch (target) {
      case 'PROD':
        return (
          <ParagraphComponent>
            {ownDictionary.prodAgreementText()}
          </ParagraphComponent>
        );
      case 'PARTNER':
        return (
          <ParagraphComponent>
            {ownDictionary.partnerAgreementText()}
          </ParagraphComponent>
        );
    }
  }, [ownDictionary, target]);

  return (
    <ContentLayoutComponent
      title={
        <TextComponent size="l" family="futura">
          {titleText}
        </TextComponent>
      }
      body={
        <>
          <TextComponent>{descriptionText}</TextComponent>
          <MarginComponent top={44} />
          <Fade
            duration={ANIMATION_DEFAULT_DURATION}
            delay={!emailProviderSelected ? ANIMATION_DEFAULT_DURATION : 0}
            when={!emailProviderSelected}
            mountOnEnter
            unmountOnExit
            collapse
          >
            <ImplicitAuthProviderForm
              target={target}
              onEmailProviderSelected={openEmailForm}
            />
          </Fade>
          <Fade
            when={emailProviderSelected}
            duration={ANIMATION_DEFAULT_DURATION}
            delay={emailProviderSelected ? ANIMATION_DEFAULT_DURATION : 0}
            mountOnEnter
            unmountOnExit
            collapse
          >
            <ImplicitAuthEmailForm onEmailConfirmed={onEmailConfirmed} />
          </Fade>
        </>
      }
      backAction={
        emailProviderSelected && (
          <BackControlComponent onPress={openProviderForm} />
        )
      }
    />
  );
};
